import { useEffect, useState, useMemo } from 'react';
import { useCart } from '@shared/cart-react';
import { useProducts } from '@shared/react-product';
import { format, set } from 'date-fns';
import { Form, Formik } from 'formik';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { isEmpty, isUndefined } from 'lodash';
import { createCommaList, roundTo } from '../../../helpers/tools';
import PrettyPrice from '../PrettyPrice';
import Price from '../Price';
import CartBreakdown from './CartBreakdown';
import { Button, cn, Dots, Icon, Input, SmoothMount } from '@shared/cove-ui';
import { cx } from 'class-variance-authority';
import toast from 'react-hot-toast';

const getNumberWithOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];

  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getDate = () => {
  const day = new Date().getDate();
  if (day > 28) {
    return '1st';
  }

  return getNumberWithOrdinal(day);
};

const getFullDate = (freeMonths: number) => {
  let date = new Date().getDate();
  let month = new Date().getMonth();
  if (date > 28) {
    date = 1;
    month += 1;
  }
  month += freeMonths;
  const finalDate = set(new Date(), { month, date });
  return format(finalDate, 'MM.dd.yy');
};

const PRODUCT_DISPLAY_NAMES = {
  'CP-PANEL-345-1': 'Cove Touch Panel',
  'CP-KEY-345-1': 'Key Remote',
  '2G-CO-345-1': 'CO Detector',
  '2G-SHF-345-1': 'Smoke Detector',
};

type Cart = ReturnType<typeof useCart>;

export type CartTotalsWidgetProps = {
  maximize?: boolean;
  showSavings?: boolean;
  productPackage?: { name: string; products: [] };
  cart: Cart;
  variant?: 'normal' | 'breakdown';
  showPromoInput?: boolean;
  showEquipmentBreakdown?: boolean;
  showDiscountBreakdown?: boolean;
  autoAppliedDiscountTitle?: string;
  useOrder?: boolean;
};

const CartTotalsWidget: React.FC<CartTotalsWidgetProps> = ({
  maximize = false,
  showSavings,
  productPackage,
  showPromoInput = false,
  cart: incomingCart,
  variant = 'normal',
  showEquipmentBreakdown = false,
  showDiscountBreakdown = false,
  autoAppliedDiscountTitle,
  useOrder = false,
}) => {
  const isBreakdown = variant === 'breakdown';
  const [expanded, setExpanded] = useState(maximize);
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : incomingCart;

  useEffect(() => {
    setExpanded(maximize);
  }, [maximize]);

  useEffect(() => {
    if (
      isEmpty(incomingCart) &&
      (isEmpty(incomingCart?.monitoringPlan) ||
        isUndefined(incomingCart?.monitoringPlan))
    ) {
      incomingCart?.updateMonitoringPlan?.('vm-pn');
    }
  }, [incomingCart, incomingCart?.monitoringPlan?.sku]);

  const totals = cart?.totals?.totals || {
    orderGrandTotal: 0,
    orderRetailTotal: 0,
    orderTotalWithTax: 0,
    equipmentTotalTax: 0,
    equipmentBaseTotal: 0,
    allEquipmentGrandTotal: 0,
    equipmentGrandTotal: 0,
    equipmentRetailTotal: 0,
    equipmentTotalWithTax: 0,
    monitoringTotalWithTax: 0,
    monitoringRetailTotalTax: 0,
    payLaterBalanceWithTax: 0,
  };
  const isTaxCalculated = cart?.taxCalculated || false;
  const totalDiscount = totals?.orderRetailTotal - totals?.orderTotalWithTax;

  const hasAffirmTransaction =
    typeof cart?.affirmAmount === 'number' && cart?.affirmAmount > 0;

  return (
    <div className="mx-auto mt-0 w-full max-w-[500px]">
      <div className="border-cove-navy-blue xs:p-cove-25 py-cove-25 px-cove-15 rounded border bg-white shadow">
        {isBreakdown ? (
          <CartBreakdown />
        ) : (
          <div className="w-full">
            <div className="mb-cove-15 flex items-center justify-between">
              <div>
                <span className="xs:items-center xs:flex-row xs:gap-cove-5 flex flex-col items-start gap-0">
                  <h5 className="text-h5">Equipment</h5>
                  <SmoothMount height={false} fade show={isTaxCalculated}>
                    <span className="text-cove-navy-blue m-0 text-xs">
                      Including tax of: ${roundTo(totals.equipmentTotalTax)}
                    </span>
                  </SmoothMount>
                </span>
              </div>
              <div aria-hidden="true" className="flex items-start">
                <Price
                  price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
                  salePrice={parseFloat(`${totals.equipmentTotalWithTax || 0}`)}
                />
              </div>
              <p className="sr-only">{`$${roundTo(
                totals.equipmentGrandTotal
              )}, regularly $${roundTo(totals.equipmentBaseTotal)}`}</p>
            </div>
            <SmoothMount fade show={showEquipmentBreakdown}>
              <div className="mb-cove-15 border-cove-dark-grey px-cove-10 py-cove-15 relative border">
                <EquipmentBreakdown
                  useOrder={useOrder}
                  productPackage={productPackage}
                />
              </div>
            </SmoothMount>

            <EquipmentDetails />

            <Monitoring useOrder={useOrder} />
            {showSavings && (
              <div className="mb-cove-15 flex items-center justify-between">
                <h5 className="text-h5">Today’s Savings</h5>
                <p className="text-cove-green ml-cove-5 text-right font-semibold">
                  $
                  {roundTo(
                    totals.equipmentBaseTotal - totals.equipmentGrandTotal
                  )}
                </p>
              </div>
            )}
            <div className="mb-cove-15 flex items-center justify-between">
              <div className="gap-cove-5 flex items-center">
                <h5 className="text-h5">Shipping</h5>
                <span className="text-cove-navy-blue m-0 text-xs">
                  2 - 7 business days
                </span>
              </div>
              <p className="text-cove-green ml-cove-5 text-right font-semibold">
                Free
              </p>
            </div>
            {showDiscountBreakdown && (
              <PromoCodeBreakdown
                useOrder={useOrder}
                autoAppliedDiscountTitle={autoAppliedDiscountTitle}
              />
            )}
            <div className="mb-cove-15 flex items-center justify-between">
              <h5 className="text-h5">Discount total</h5>
              <span className="text-cove-red">
                -<PrettyPrice price={totalDiscount || 0} />
              </span>
            </div>
            <div className="mb-cove-15 flex items-center justify-between">
              <h5 className="text-h5">Today&apos;s Total</h5>
              <Price
                salePrice={
                  cart?.getTodaysGrandTotal?.() ||
                  totals?.orderTotalWithTax ||
                  0
                }
              />
            </div>

            <div
              className={cx(
                'flex items-center justify-between',
                maximize ? 'mb-cove-0' : 'mb-cove-15'
              )}
            >
              <h2 className="text-h5">Order Total</h2>
              <Price
                price={parseFloat(`${totals.equipmentRetailTotal || 0}`)}
                salePrice={
                  cart?.getTodaysGrandTotal?.() || totals.orderTotalWithTax || 0
                }
              />
            </div>

            {hasAffirmTransaction && (
              <div className="mb-cove-15 flex items-center justify-between">
                <h5 className="text-h5">Affirm</h5>
                <div className="flex items-center">
                  -<PrettyPrice price={cart?.affirmAmount || 0} />
                </div>
              </div>
            )}
            {!maximize && (
              <>
                <SmoothMount
                  fade
                  showOverflow
                  show={!expanded}
                  className="flex w-full flex-col justify-center"
                >
                  <Button
                    id="review-order-widget"
                    variant="text"
                    color="blue"
                    className="mx-auto text-center underline"
                    onClick={() => {
                      setExpanded(!expanded);
                    }}
                  >
                    {expanded ? 'Hide' : 'Review'} order
                  </Button>
                </SmoothMount>

                <SmoothMount fade show={expanded}>
                  <div
                    className={cn(
                      'border-cove-dark-grey px-cove-10 py-cove-15 relative border',
                      hasAffirmTransaction &&
                        'mb-cove-15 pb-cove-15 border-b-cove-dove border-b'
                    )}
                  >
                    <button
                      onClick={() => setExpanded(false)}
                      aria-label="Minimize order"
                      className="text-cove-blue absolute right-0 top-0 m-0 cursor-pointer border-none bg-transparent p-0"
                    >
                      <Icon icon="Cancel" width={32} height={32} />
                    </button>
                    <EquipmentBreakdown
                      useOrder={useOrder}
                      productPackage={productPackage}
                    />
                  </div>
                </SmoothMount>
              </>
            )}
            {showPromoInput && <PromoCodeInput />}
          </div>
        )}
      </div>
    </div>
  );
};

const useOrderOrCart = () => {
  const currentCart = useCart();
  const [order, setOrder] = useState<null | Cart>(null);
  const cart = order || currentCart;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const order = JSON.parse(localStorage.getItem('ORDER') || '{}');
      if (order.cart) {
        setOrder(order.cart as Cart);
      }
    }
  }, [currentCart]);

  return cart;
};

const EquipmentDetails = () => {
  const cart = useCart();
  const products = useProducts();

  // Just show equipment
  const exclude = [
    'window-sticker-sm',
    'vm-pn',
    'CP-YS-23i-1',
    'bm-pn',
    'bm-pl',
    'vm-pl',
    'vm-gp',
  ];

  const [expanded, setExpanded] = useState(false);

  const equipment = useMemo(() => {
    return Object.values(cart.cartProducts).filter(
      product => !exclude.includes(product.sku)
    );
  }, [cart.cartProducts]);

  const panel = equipment?.find(product => product.sku === 'CPA-PANEL-345-1');
  const hub = equipment?.find(product => product.sku === 'CPA-HUB-345-1');

  // --- Put panel and hub at the top ---
  const orderedEquipment = [...equipment]?.filter(
    product =>
      product.sku !== 'CPA-PANEL-345-1' && product.sku !== 'CPA-HUB-345-1'
  );

  if (panel && hub) {
    orderedEquipment.unshift(hub, panel);
  }
  // ---

  return equipment.length > 0 ? (
    <div className="mb-cove-15 flex w-full flex-col">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-cove-blue mb-cove-10 flex items-center bg-transparent text-sm font-medium"
      >
        {expanded ? 'Hide' : 'Show'} equipment details{' '}
        <Icon
          className={cn(
            'transition-all duration-500 ease-in-out',
            expanded && 'rotate-180'
          )}
          icon="Caret"
        />
      </button>
      {cart?.cartProducts && (
        <SmoothMount
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          show={expanded}
        >
          {orderedEquipment?.map((product, i) => {
            const { sku, qty, name } = product;
            return (
              <div
                key={product.sku}
                className={cx(
                  'border-cove-dove py-cove-5 px-cove-2 flex w-full justify-between',
                  i !== 0 && 'border-t'
                )}
              >
                <div className="gap-cove-15 flex items-center">
                  <span className="flex h-[45px] w-[45px] items-center justify-center">
                    <img
                      className="max-w-[45px]"
                      src={products[sku].primaryImage?.url}
                      alt={name}
                    />
                  </span>
                  <p className="text-cove-navy-blue text-base">{name}</p>
                </div>
                <p className="text-cove-navy-blue self-center text-base">
                  {qty}
                </p>
              </div>
            );
          })}
        </SmoothMount>
      )}
    </div>
  ) : null;
};

const Monitoring = ({ useOrder = false }: { useOrder?: boolean }) => {
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const [path, setPath] = useState('');

  const isTaxCalculated = cart?.taxCalculated || false;
  const freeMonths = cart?.freeMonths;
  const totals = cart?.totals?.totals || {
    monitoringTotalWithTax: 0,
    monitoringRetailTotalTax: 0,
  };
  const monitoringBasePrice = totals.monitoringTotalWithTax;
  const isFirstMonthFree = cart?.appliedDiscounts?.some(
    (id: string) => id === '141785456' || id === '141785457'
  );

  useEffect(() => {
    const path =
      typeof window !== 'undefined' ? window.document.location.pathname : '';
    setPath(path);
  }, []);

  return (
    <div className="mb-cove-15">
      <div className="mt-cove-15 flex items-center justify-between">
        <h5 className="text-h5">Monitoring</h5>
        {freeMonths ? (
          <div className="flex">
            <PrettyPrice price={monitoringBasePrice || 0} />
            <p className="text-cove-green ml-cove-5 text-right font-semibold">
              {freeMonths === 1
                ? '1st Month Free'
                : `${freeMonths} Months Free`}
            </p>
          </div>
        ) : (
          <p className="text-cove-green ml-cove-5 text-right font-semibold">
            ${totals.monitoringTotalWithTax}
          </p>
        )}
      </div>
      <SmoothMount
        show={isTaxCalculated}
        className="flex max-w-[240px] flex-col"
      >
        <span className="text-cove-navy-blue m-0 text-xs leading-[18px]">
          {!path.includes('benefits') &&
          !path.includes('thanks') &&
          (cart?.monitoringPlan?.name || '').match(/plus/i) &&
          isFirstMonthFree &&
          !(cart?.monitoringPlan?.sku || 'vm-pn').split('-')[1].includes('pl')
            ? 'First month free. '
            : ''}
          Auto-pay ${totals.monitoringTotalWithTax} on the {getDate()} of each
          month
          {freeMonths ? (
            <span className="text-cove-navy-blue m-0 text-xs">
              , starting on {getFullDate(freeMonths)}.
            </span>
          ) : (
            '.'
          )}
        </span>
        {isTaxCalculated ? (
          <span className="text-cove-navy-blue m-0 text-xs">
            Including tax of: ${roundTo(totals?.monitoringRetailTotalTax || 0)}
          </span>
        ) : (
          <i className="text-cove-navy-blue m-0 text-xs">
            Applicable tax calculated at next step
          </i>
        )}
      </SmoothMount>
    </div>
  );
};

const EquipmentBreakdown = ({
  productPackage,
  useOrder = false,
}: {
  productPackage: { name: string; products: [] } | undefined;
  useOrder?: boolean;
}) => {
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const products = useProducts();
  const packageItems = productPackage
    ? productPackage.products.map((product: { sku: string }) => product.sku)
    : [];

  return (
    <>
      {productPackage &&
        `The ${
          productPackage.name.charAt(0).toUpperCase() +
          productPackage.name.slice(1)
        }`}
      <div className="xxs:grid-cols-2 xs:grid-cols-3 grid grid-cols-1">
        {!isUndefined(cart?.cartItemIds) &&
          cart?.cartItemIds?.map(sku => {
            const product = products[sku];
            const cartProduct = cart?.cartProducts?.[sku];
            if (
              product?.productType === 'equipment' &&
              packageItems.includes(product.sku)
            ) {
              return (
                <p
                  key={product.sku}
                  className="text-cove-navy-blue m-0 text-xs"
                >
                  {cartProduct?.qty} {product.name}
                </p>
              );
            }
          })}
        {cart?.cartItemIds
          ?.map(sku => products[sku])
          ?.sort((a, b) => a.position - b.position)
          ?.map(product => {
            const cartProduct = cart?.cartProducts?.[product.sku];
            if (
              product?.productType === 'equipment' &&
              !packageItems.includes(product.sku) &&
              cartProduct &&
              cartProduct?.qty > 0
            ) {
              return (
                <p
                  className="text-cove-navy-blue m-0 text-xs leading-4"
                  key={product.sku}
                >
                  {cartProduct?.qty}{' '}
                  {PRODUCT_DISPLAY_NAMES[
                    product.sku as keyof typeof PRODUCT_DISPLAY_NAMES
                  ] || product.name}
                  {cartProduct?.qty > 1 && product.name.slice(-1) !== 's'
                    ? 's'
                    : ''}
                </p>
              );
            }
          })}
      </div>
    </>
  );
};

const autoAppliedDiscountIdTitleMap = {
  '141785457': 'First month free',
  '141785456': 'First month free',
  '960179': 'Sale',
};

export const PromoCodeBreakdown = ({
  autoAppliedDiscountTitle,
  useOrder = false,
}: {
  autoAppliedDiscountTitle?: string;
  useOrder?: boolean;
}) => {
  const [canRemovePromo, setCanRemovePromo] = useState(true);
  const [showPromoList, setShowPromoList] = useState(false);
  const [promoBusy, setPromoBusy] = useState<string | null>(null);
  const currentCart = useCart();
  const orderOrCart = useOrderOrCart();
  const cart = useOrder ? orderOrCart : currentCart;
  const [promoCodesApplied, setPromoCodesApplied] = useState(
    cart.discountBreakdown || []
  );

  const hasPromoCode = promoCodesApplied.length > 0;

  useEffect(() => {
    setPromoCodesApplied(cart?.calculateCouponBreakdown?.() || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.totals?.totals?.orderTotalWithTax]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (hasPromoCode) {
      timeout = setTimeout(() => {
        setShowPromoList(true);
      }, 200);
    } else {
      if (timeout) {
        clearTimeout(timeout);
      }
      setShowPromoList(false);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasPromoCode]);

  return (
    <MotionConfig>
      <AnimatePresence>
        {hasPromoCode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-cove-15 overflow-hidden"
          >
            <div>
              <h5 className="text-h5">Promo code</h5>
              <div
                className={`mt-cove-10 px-cove-10 overflow-hidden border ${showPromoList ? 'border-cove-dove' : 'border-transparent'} transition-all`}
              >
                <AnimatePresence>
                  {showPromoList &&
                    promoCodesApplied.map(
                      (
                        {
                          couponCodeId,
                          couponCode,
                          couponCodeDisplayText,
                          totalSavings,
                          description,
                        },
                        index
                      ) => {
                        const isAutoAppliedEquipment =
                          couponCodeId === 'auto-applied-960179';
                        const isAutoApplied =
                          couponCodeId.includes('auto-applied');
                        const strippedPromoCodeId = couponCodeId.replace(
                          'auto-applied-',
                          ''
                        );
                        return (
                          <motion.div
                            key={couponCode}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div
                              className={cn(
                                'py-cove-10 bg-white',
                                index > 0 && 'border-t-cove-dove border-t'
                              )}
                            >
                              <div className="gap-cove-15 md:gap-cove-50 mb-cove-5 flex items-start justify-between text-sm">
                                <p className="m-0 shrink truncate font-bold">
                                  {isAutoAppliedEquipment &&
                                  autoAppliedDiscountTitle
                                    ? autoAppliedDiscountTitle
                                    : isAutoApplied
                                      ? autoAppliedDiscountIdTitleMap[
                                          strippedPromoCodeId as keyof typeof autoAppliedDiscountIdTitleMap
                                        ] || 'Discount'
                                      : (couponCodeDisplayText || '')?.length >
                                          0
                                        ? couponCodeDisplayText
                                        : couponCode}
                                </p>
                                <p className="text-cove-red m-0 shrink-0 font-normal">
                                  -<PrettyPrice price={totalSavings} />
                                </p>
                              </div>
                              <div className="gap-cove-50 flex justify-between text-sm">
                                <p className="m-0">{description}</p>

                                {!isAutoApplied && (
                                  <button
                                    onClick={async () => {
                                      setCanRemovePromo(false);
                                      setPromoBusy(couponCode);
                                      await cart
                                        .removePromo(couponCode)
                                        .then(() => {
                                          setPromoBusy(null);
                                          setCanRemovePromo(true);
                                        });
                                    }}
                                    disabled={!canRemovePromo}
                                    className="m-0 flex w-max min-w-[55px] flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-inherit underline"
                                  >
                                    {promoBusy === couponCode ? (
                                      <Dots
                                        className="bg-cove-blue h-2 w-2"
                                        gap={3}
                                      />
                                    ) : (
                                      'Remove'
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      }
                    )}
                </AnimatePresence>
              </div>
              <div className="pb-cove-15" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};

const PromoCodeInput = () => {
  const cart = useCart();

  return (
    <Formik
      initialValues={{ promo: '' }}
      onSubmit={async (values, actions) => {
        await cart
          .applyPromo(values.promo)
          .then(res => {
            const {
              isValid,
              isActive,
              allCouponDiscountsAlreadyApplied,
              couponAlreadyApplied,
              missingProducts,
              couponExclusive,
              minimumTotal,
            } = res;

            if (couponExclusive) {
              actions.setFieldError(
                'promo',
                'This coupon cannot be combined with other offers'
              );
              return;
            }

            if (!isActive || !isValid) {
              actions.setFieldError('promo', `Please enter a valid promo code`);
              return;
            }

            if (allCouponDiscountsAlreadyApplied) {
              actions.setFieldError(
                'promo',
                'All discounts related to this promo code have already been applied'
              );
              return;
            }

            if (couponAlreadyApplied) {
              actions.setFieldError('promo', 'Promo code already applied');
              return;
            }

            if (missingProducts.length > 0) {
              const productNames = missingProducts
                .map((sku: string) => {
                  const product = cart?.products?.[sku];
                  if (!product) {
                    return undefined;
                  }
                  return product.name;
                })
                .filter(name => !!name) as Array<string>;
              actions.setFieldError(
                'promo',
                `Your cart is not yet eligible for this coupon. Add a ${createCommaList(productNames)} before applying this coupon.`
              );
              return;
            }

            if (minimumTotal > 0) {
              toast(
                `The coupon has been applied, but it requires a minimum order total of $${minimumTotal}. Your order total has been updated.`
              );
            }

            cart.calculateCouponBreakdown();
            actions.setFieldValue('promo', '');

            return;
          })
          .catch(err => console.log({ err }));
      }}
    >
      {({ values, isSubmitting, errors, setFieldValue }) => {
        return (
          <Form>
            <div className="gap-cove-15 mt-cove-25 flex items-start [&_div]:mb-0 [&_div]:min-w-[unset]">
              <Input
                name="promo"
                placeholder="Promo Code"
                value={values.promo}
                onChange={e => setFieldValue('promo', e.target.value)}
                className="rounded-none"
                invalid={!!errors.promo}
              />
              <Button
                type="submit"
                variant="outlined"
                disabled={values.promo.length === 0 || isSubmitting}
                loading={isSubmitting}
                className="h-9 w-[110px] font-medium"
              >
                Apply
              </Button>
            </div>
            <SmoothMount show={!!errors.promo} fade>
              <p className="text-cove-red mb-0 text-sm">{errors.promo}</p>
            </SmoothMount>
          </Form>
        );
      }}
    </Formik>
  );
};

export { PromoCodeInput };
export default CartTotalsWidget;
